import { useMemo } from 'react'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded'
import { StatusChip } from '../../Common'
import { Button } from '../../Common/Button'
import { IconButton } from '../../Common/IconButton'
import { Pressable } from '../../Common/Pressable'
import { compareIsoDate, eventColorVar, toIsoDate, type CalendarEvent } from '../calendarHelpers'

interface AgendaViewProps {
  focusedDate: Date
  allDayEvents: CalendarEvent[]
  timedEvents: CalendarEvent[]
  onEventClick: (event: CalendarEvent) => void
  onCellClick: (date: Date) => void
}

const KIND_LABEL: Record<CalendarEvent['kind'], string> = {
  goal: 'Goal',
  task: 'Task',
  subtask: 'SubTask',
  checklist: 'Checklist',
  meeting: 'Meeting',
  huddle: 'Huddle',
  session: 'Session',
  edu_event: 'Event',
  edu_ptm: 'PTM',
}

// §8.4.9 — the old logic did `date.toLocaleDateString('en', { weekday:
// 'long', day: 'numeric' }).split(' ')[0]` and then rendered the day number
// AGAIN as an ordinal, so at some locales the header collapsed to
// "28 28th". Ask specifically for the weekday and never depend on how the
// locale glues weekday + day back together.
const fmtDayHeader = (date: Date) =>
  date.toLocaleDateString('en', { weekday: 'long' })

const ordinalDate = (date: Date) => {
  const day = date.getDate()
  const rem10 = day % 10
  const rem100 = day % 100
  if (rem100 >= 11 && rem100 <= 13) return `${day}th`
  if (rem10 === 1) return `${day}st`
  if (rem10 === 2) return `${day}nd`
  if (rem10 === 3) return `${day}rd`
  return `${day}th`
}

const fmtMonthYear = (date: Date) =>
  date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })

export const AgendaView = ({
  focusedDate,
  allDayEvents,
  timedEvents,
  onEventClick,
  onCellClick,
}: AgendaViewProps) => {
  const iso = useMemo(() => toIsoDate(focusedDate), [focusedDate])

  const dayAllDay = useMemo(
    () =>
      allDayEvents.filter(
        (event) =>
          compareIsoDate(event.startDate, iso) <= 0 && compareIsoDate(event.endDate, iso) >= 0,
      ),
    [allDayEvents, iso],
  )
  const dayTimed = useMemo(
    () =>
      timedEvents
        .filter((event) => event.startDate === iso)
        .sort((a, b) => (a.startTime ?? '').localeCompare(b.startTime ?? '')),
    [timedEvents, iso],
  )

  const totalCount = dayAllDay.length + dayTimed.length
  const weekday = fmtDayHeader(focusedDate)
  const ordinal = ordinalDate(focusedDate)

  return (
    <div className="calAgenda" data-cal-view="agenda">
      <header className="calAgendaHead">
        <div className="calAgendaHeadTitles">
          <p className="calAgendaHeadMonth">{fmtMonthYear(focusedDate)}</p>
          <h2>
            {weekday}
            <sup>{ordinal}</sup>
          </h2>
          <p className="calAgendaHeadCount">
            {totalCount === 0
              ? 'Nothing scheduled'
              : `${totalCount} ${totalCount === 1 ? 'item' : 'items'}`}
          </p>
        </div>
        <IconButton
          variant="tertiary"
          size="sm"
          onClick={() => onCellClick(focusedDate)}
          label="Add checklist on this date"
          icon={<AddRoundedIcon fontSize="small" />}
        />
      </header>

      {totalCount === 0 ? (
        <div className="calAgendaEmpty">
          <div className="calAgendaEmptyIcon" aria-hidden="true">
            <EventAvailableRoundedIcon fontSize="large" />
          </div>
          <p className="calAgendaEmptyTitle">Your day is clear</p>
          <p className="calAgendaEmptyHint">Nothing on the calendar for {weekday}.</p>
          <Button
            variant="primary"
            icon={<AddRoundedIcon fontSize="small" />}
            onClick={() => onCellClick(focusedDate)}
          >
            Add a checklist
          </Button>
        </div>
      ) : (
        <ul className="calAgendaList">
          {dayAllDay.length > 0 && (
            <li className="calAgendaGroupLabel" aria-hidden="true">All-day</li>
          )}
          {dayAllDay.map((event) => (
            <li
              key={event.id}
              className={`calAgendaRow calAgendaRow--allDay calAgendaRow--${event.kind}`}
              style={{ ['--cal-accent' as string]: eventColorVar(event.kind) } as React.CSSProperties}
            >
              <Pressable
                className="calAgendaCard"
                onClick={() => onEventClick(event)}
              >
                <span className="calAgendaCardTop">
                  <span className="calAgendaKind">{KIND_LABEL[event.kind]}</span>
                  <StatusChip tone="neutral" size="sm">All-day</StatusChip>
                </span>
                <span
                  className={`calAgendaTitle${event.isCancelled ? ' isCancelled' : ''}`}
                >
                  {event.title}
                </span>
              </Pressable>
            </li>
          ))}
          {dayTimed.length > 0 && (
            <li className="calAgendaGroupLabel" aria-hidden="true">Scheduled</li>
          )}
          {dayTimed.map((event) => (
            <li
              key={event.id}
              className={`calAgendaRow calAgendaRow--timed calAgendaRow--${event.kind}`}
              style={{ ['--cal-accent' as string]: eventColorVar(event.kind) } as React.CSSProperties}
            >
              <div className="calAgendaTime">
                <span className="calAgendaTimeMain">{event.startTime}</span>
                {event.endTime && <span className="calAgendaTimeEnd">– {event.endTime}</span>}
              </div>
              <Pressable
                className="calAgendaCard"
                onClick={() => onEventClick(event)}
              >
                <span className="calAgendaCardTop">
                  <span className="calAgendaKind">{KIND_LABEL[event.kind]}</span>
                </span>
                <span
                  className={`calAgendaTitle${event.isCancelled ? ' isCancelled' : ''}`}
                >
                  {event.title}
                </span>
              </Pressable>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
